import React, { useState } from 'react';
import axios from 'axios';
import MaterialTable from 'material-table';
import { ToastContainer, toast } from 'react-toastify';
import { API } from '../../backend';
import authHeader from '../../services/auth-header';

// ─── Constants ───────────────────────────────────────────────────────────────

const ENDPOINTS = [
  { label: 'Accounts',         value: 'accounts' },
  { label: 'Applications',     value: 'applications' },
  { label: 'Contracts',        value: 'contracts' },
  { label: 'Contract Ledger',  value: 'contracts/ledger' },
  { label: 'Equipment',        value: 'equipment' },
  { label: 'Contacts',         value: 'contacts' },
  { label: 'Data Tape (JSON)', value: 'datatape' },
];

const EXAMPLES = {
  accounts:           [{ account_id: 'ACC-001', account_name: 'Acme Corp', status: 'Active', balance: 15000.00, created_at: '2024-01-15' }],
  applications:       [{ application_id: 'APP-101', applicant_name: 'John Doe', status: 'Pending', amount_requested: 50000, submitted_at: '2024-03-10' }],
  contracts:          [{ contract_id: 'CON-201', customer: 'Acme Corp', contract_type: 'Lease', total_amount: 120000, start_date: '2024-01-01', end_date: '2026-12-31' }],
  'contracts/ledger': [{ ledger_id: 'LED-301', contract_id: 'CON-201', transaction_date: '2024-04-01', debit: 1000.00, credit: 0, balance: 119000.00 }],
  equipment:          [{ equipment_id: 'EQP-401', description: 'Forklift Model X', serial_number: 'SN-98765', condition: 'New', value: 45000.00 }],
  contacts:           [{ contact_id: 'CNT-501', first_name: 'Jane', last_name: 'Smith', email: 'jane.smith@acme.com', phone: '+1-555-0100', role: 'Finance Manager' }],
  datatape:           [{ tape_id: 'DT-601', contract_id: 'CON-201', period: '2024-Q1', principal: 10000, interest: 450, total_payment: 10450 }],
};

// ─── Collapsible JSON Tree ────────────────────────────────────────────────────

const S = {
  key:    { color: '#1d4ed8', fontWeight: 700 },
  str:    { color: '#15803d' },
  num:    { color: '#7c3aed' },
  bool:   { color: '#b45309' },
  nul:    { color: '#b91c1c' },
  muted:  { color: '#94a3b8', fontSize: 11 },
  toggle: {
    cursor: 'pointer', userSelect: 'none', display: 'inline-flex',
    alignItems: 'center', justifyContent: 'center',
    width: 16, height: 16, borderRadius: 3, marginRight: 4,
    background: '#e2e8f0', color: '#475569', fontSize: 11, fontWeight: 700,
    border: 'none', padding: 0, flexShrink: 0,
  },
};

function Primitive({ value }) {
  if (value === null)            return <span style={S.nul}>null</span>;
  if (typeof value === 'boolean') return <span style={S.bool}>{String(value)}</span>;
  if (typeof value === 'number')  return <span style={S.num}>{value}</span>;
  return <span style={S.str}>"{value}"</span>;
}

function JsonNode({ data, keyName, depth = 0, defaultOpen = true }) {
  const [open, setOpen] = useState(depth < 2 ? defaultOpen : false);
  const indent = depth * 16;
  const isArr = Array.isArray(data);
  const isObj = data !== null && typeof data === 'object';

  if (!isObj) {
    return (
      <div style={{ paddingLeft: indent, lineHeight: '1.8', fontFamily: 'Consolas, monospace', fontSize: 13 }}>
        {keyName !== undefined && <><span style={S.key}>"{keyName}"</span><span style={S.muted}>: </span></>}
        <Primitive value={data} />
      </div>
    );
  }

  const entries = isArr ? data.map((v, i) => [i, v]) : Object.entries(data);
  const openBracket  = isArr ? '[' : '{';
  const closeBracket = isArr ? ']' : '}';
  const preview = isArr
    ? `${data.length} item${data.length !== 1 ? 's' : ''}`
    : `${entries.length} key${entries.length !== 1 ? 's' : ''}`;

  return (
    <div style={{ paddingLeft: indent, fontFamily: 'Consolas, monospace', fontSize: 13, lineHeight: '1.8' }}>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
        <button style={S.toggle} onClick={() => setOpen(o => !o)}>{open ? '▾' : '▸'}</button>
        {keyName !== undefined && <><span style={S.key}>"{keyName}"</span><span style={S.muted}>: </span></>}
        <span style={S.muted}>{openBracket}</span>
        {!open && (
          <>
            <span style={{ ...S.muted, marginLeft: 4, fontStyle: 'italic' }}>{preview}</span>
            <span style={S.muted}>{closeBracket}</span>
          </>
        )}
      </div>

      {open && (
        <>
          {entries.map(([k, v]) => (
            <JsonNode key={k} data={v} keyName={isArr ? undefined : k} depth={depth + 1} />
          ))}
          <div style={{ lineHeight: '1.8' }}>
            <span style={S.muted}>{closeBracket}</span>
          </div>
        </>
      )}
    </div>
  );
}

function JsonTreeViewer({ data, maxHeight = '560px' }) {
  const [copied, setCopied] = useState(false);
  const [expandAll, setExpandAll] = useState(null); // null | true | false — used as key reset trick

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{ borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
      {/* header bar */}
      <div style={{ background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', padding: '6px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#f59e0b', display: 'inline-block' }} />
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
          <span style={{ marginLeft: 8, fontSize: 11, color: '#64748b', fontFamily: 'monospace' }}>application/json</span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            onClick={() => setExpandAll(k => k === true ? null : true)}
            style={{ fontSize: 11, padding: '2px 10px', borderRadius: 4, background: '#f8fafc', color: '#475569', border: '1px solid #cbd5e1', cursor: 'pointer' }}
          >
            Expand All
          </button>
          <button
            onClick={() => setExpandAll(k => k === false ? null : false)}
            style={{ fontSize: 11, padding: '2px 10px', borderRadius: 4, background: '#f8fafc', color: '#475569', border: '1px solid #cbd5e1', cursor: 'pointer' }}
          >
            Collapse All
          </button>
          <button
            onClick={handleCopy}
            style={{ fontSize: 11, padding: '2px 12px', borderRadius: 4, background: copied ? '#16a34a' : '#0d6efd', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600 }}
          >
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        </div>
      </div>

      {/* tree body */}
      <div
        key={String(expandAll)}
        style={{ background: '#fff', maxHeight, overflowY: 'auto', padding: '12px 16px' }}
      >
        <JsonNode data={data} depth={0} defaultOpen={expandAll !== false} />
      </div>
    </div>
  );
}

// ─── Table helpers ────────────────────────────────────────────────────────────

function buildColumns(data) {
  if (!Array.isArray(data) || data.length === 0) return [];
  return Object.keys(data[0]).map((key) => ({
    title: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    field: key,
    render: (row) => {
      const val = row[key];
      if (val === null || val === undefined) return '—';
      if (typeof val === 'object') return JSON.stringify(val);
      return String(val);
    },
  }));
}

// ─── Main page ────────────────────────────────────────────────────────────────

function QuickFi() {
  const [apiKey, setApiKey]             = useState('');
  const [endpoint, setEndpoint]         = useState(ENDPOINTS[0].value);
  const [lastModified, setLastModified] = useState('');
  const [loading, setLoading]           = useState(false);
  const [rows, setRows]                 = useState([]);
  const [rawJson, setRawJson]           = useState(null);
  const [fetched, setFetched]           = useState(false);
  const [viewMode, setViewMode]         = useState('table');

  const handleFetch = async () => {
    if (!apiKey.trim()) { toast.error('Please enter your QuickFi API key.'); return; }
    setLoading(true);
    setFetched(false);
    try {
      const params = lastModified ? { lastModified } : {};
      const res = await axios.get(API + `quickfi/${endpoint}/`, {
        headers: { ...authHeader(), 'X-Api-Key': apiKey.trim() },
        params,
      });
      const data = Array.isArray(res.data)
        ? res.data
        : res.data?.data ?? res.data?.results ?? (typeof res.data === 'object' ? [res.data] : []);
      setRawJson(res.data);
      setRows(data);
      data.length === 0 ? toast.info('No records returned.') : toast.success(`Loaded ${data.length} record(s).`);
      setFetched(true);
    } catch (err) {
      toast.error(err.response?.data?.error ?? err.message ?? 'Request failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid py-4 pt-2">
      <ToastContainer />
      <div className="row py-2 mt-2">
        <div className="col-md-12">
          <h5 className="mb-3 tc">QuickFi Integration</h5>

          {/* ── Filter card ──────────────────────────────────── */}
          <div className="card mb-4 shadow-sm">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-4">
                  <label className="form-label fw-semibold">API Key <span className="text-danger">*</span></label>
                  <input type="password" className="form-control" placeholder="Enter QuickFi X-Api-Key"
                    value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-semibold">Endpoint</label>
                  <select className="form-select" value={endpoint} onChange={(e) => { setEndpoint(e.target.value); setFetched(false); setRows([]); setRawJson(null); }}>
                    {ENDPOINTS.map((ep) => <option key={ep.value} value={ep.value}>{ep.label}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-semibold">Last Modified <span className="text-muted fw-normal">(optional)</span></label>
                  <input type="date" className="form-control" value={lastModified} onChange={(e) => setLastModified(e.target.value)} />
                </div>
                <div className="col-md-2">
                  <button className="btn btn-primary w-100" onClick={handleFetch} disabled={loading}>
                    {loading ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Loading…</> : 'Fetch Data'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ── Example (before fetch) ───────────────────────── */}
          {!fetched && !loading && (
            <div className="mb-4">
              <div className="d-flex align-items-center mb-2 gap-2">
                <span className="badge bg-secondary" style={{ fontSize: 11 }}>EXAMPLE RESPONSE</span>
                <span className="text-muted" style={{ fontSize: 12 }}>
                  — what a <strong>{ENDPOINTS.find(e => e.value === endpoint)?.label}</strong> response looks like
                </span>
              </div>
              <JsonTreeViewer data={EXAMPLES[endpoint] ?? []} maxHeight="280px" />
            </div>
          )}

          {/* ── Results ──────────────────────────────────────── */}
          {fetched && (
            <>
              <div className="d-flex gap-2 mb-3 align-items-center">
                <button className={`btn btn-sm ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setViewMode('table')}>
                  Table View
                </button>
                <button className={`btn btn-sm ${viewMode === 'json' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setViewMode('json')}>
                  JSON View
                </button>
                <span className="text-muted ms-2" style={{ fontSize: 12 }}>
                  {rows.length} record(s) — <strong>/api/v1/financing/{endpoint}</strong>
                </span>
              </div>

              {viewMode === 'table' && (
                <MaterialTable
                  title={ENDPOINTS.find((e) => e.value === endpoint)?.label ?? 'Results'}
                  columns={buildColumns(rows)}
                  data={rows}
                  options={{
                    search: true, paging: true, pageSize: 10, pageSizeOptions: [10, 25, 50],
                    exportButton: true, exportAllData: true,
                    headerStyle: { fontWeight: 'bold', fontSize: 13 },
                    rowStyle: { fontSize: 12 },
                  }}
                  style={{ padding: '0.5rem', backgroundColor: 'var(--bs-table-bg)', boxShadow: 'inset 0 0 0 9999px var(--bs-table-accent-bg)' }}
                />
              )}

              {viewMode === 'json' && (
                <JsonTreeViewer data={rawJson} maxHeight="620px" />
              )}
            </>
          )}

        </div>
      </div>
    </div>
  );
}

export default QuickFi;
